define((require) => {
    "use strict";

    const AbstractWidgetView = require("shared/ui/experience/widgets/AbstractWidgetView");
    const WebFontsUtil       = require("shared/ui/experience/fonts/WebFontsUtil");
    const Browser            = require("shared/services/Browser");
    const $                  = require("jquery");

    /**
     * A regular expression that matches the <i>font-size</i> of an HTML string. Used to update
     * the font size on scale changes.
     *
     * @type {RegExp}
     */
    const FONT_SIZE_REG_EX = /font-size\s*:\s*(\d+)\s*px/gi;

    /**
     * A regular expression that matches the <i>letter-spacing</i> of an HTML string. Used to update
     * the letter-spacing on scale changes.
     *
     * @type {RegExp}
     */
    const LETTER_SPACING_REG_EX = /letter-spacing\s*:\s*(\d+)\s*px/gi;

    /**
     * Replaces \n with <br/> and updates any font sizes and letter spacings to the specified scale value.
     *
     * @param  {String} text  The HTML formatted string.
     * @param  {Number} scale The current average scale.
     *
     * @return {String} The updated HTML formatted string.
     */
    function constructHTMLText(text, scale) {
        const fontSizeHandler = (match, sizeValue) => {
            const size = Math.round(scale * parseInt(sizeValue, 10));
            return `font-size:${size}px`;
        };

        const letterSpacingHandler = (match, letterSpacingValue) => {
            const letterSpacing = Math.round(scale * parseInt(letterSpacingValue, 10));
            return `letter-spacing:${letterSpacing}px`;
        };

        return text
            .replace("\n", "<br/>")
            .replace(FONT_SIZE_REG_EX, fontSizeHandler)
            .replace(LETTER_SPACING_REG_EX, letterSpacingHandler);
    }

    /**
     * The view rendering a text widget.
     *
     * @class TextWidgetView
     * @constructor
     *
     * @cfg {TextWidgetModel} model The model of the text widget being rendered.
     */
    return AbstractWidgetView.extend("TextWidget", {

        tagName: "div",

        updateRendering(element) {
            // Always set properties that are cheap to handle
            const attributes = this.model.attributes;
            let scale = this.model.get("scale").average();

            /*
             * Hack because of Font boosting ( https://bugs.webkit.org/show_bug.cgi?id=FontBoosting ).
             * This behaviour, happening specifically on Chrome for Android, appears to be based on elements
             * with dynamic heights (as our text widget).
             * We need to reduce the scale of font size (proportional to the scale). The specific number seems to work
             * on most of scenarios, most used font sizes, which are not that big.
             */
            if (Browser.os.android) {
                scale -= 0.04;
            }

            const fontFamily = WebFontsUtil.quoteSpecialFontNames(WebFontsUtil.getCssFontFamily(this.model));

            if (this.model.hasActions()) {
                element.addClass("widget-with-action");
            }

            element.addClass(this.model.get("type"));

            const css = {
                fontFamily,
                color: attributes.color,
                fontStyle: attributes.fontStyle,
                fontWeight: attributes.fontWeight,
                fontSize: attributes.fontSize * scale,
                lineHeight: attributes.lineHeight,
                textAlign: attributes.textAlign,
                letterSpacing: attributes.letterSpacing * scale,
                textDecoration: attributes.textDecoration,
                textTransform: attributes.textTransform,
                fontVariant: attributes.fontVariant,
                textShadow: attributes.textShadow
            };

            // The "new" text widget wants HTML breaks
            const $text = $("<div>").append(constructHTMLText(attributes.text, scale));

            element.css(css);

            // In chrome, we have an issue with font being wonky when it is rotated. The following trick will
            // mitigate this problem but with a drawback that the font is a bit blurry. So the optimized solution
            // is to only apply this to the rotated ones.
            element.toggleClass("rotated", attributes.rotation !== 0);

            // Remove script tags from the text
            $text.find("script").remove();

            // Apply the text
            element.html($text.html());
        },

        onShow() {
            this.__super();

            // Defer the IE font hack method to ensure the deferred
            // methods in TextWidgetViewEditorMixin and AbstractWidgetView are fired
            setTimeout(() => {
                this.ieFontHack();
            }, 0);
        },

        /**
         * IE11 on Win7+8 does not render nested fonts correctly.
         * By removing and re-adding the quotes to the inline font family style
         * the fonts are re-rendered correctly
         */
        ieFontHack() {
            if (!Browser.type.ie) {
                return;
            }

            const fontElements = this.$el.find("span");

            if (fontElements.length === 0) {
                return;
            }

            fontElements.each((index, element) => {
                const elementFontFamily = element.style.fontFamily;

                if (!elementFontFamily) {
                    return;
                }

                // Fonts are already loaded due to the initial font family style
                // Now remove the font family to enforce a re-render
                element.style.fontFamily = "";

                // Remove quotes and re-add them as single quotes as this triggers a re-render in IE
                const newFontFamily = elementFontFamily.split(",").map((familyName) => {
                    const nameWithoutQuotes = familyName.replace(/['"]+/g, "");
                    return `'${nameWithoutQuotes}'`;
                }).join(",");

                element.style.fontFamily = newFontFamily;
            });
        }

    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/textWidget/TextWidgetView.js